import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import DashHeader from "../../components/DashHeader";
import { fetchWithTokenRefresh } from "../../utils/fetchWithTokenRefresh";
import { triggerCreditUpdate } from "../../utils/creditManager";
import VideoTaskbar from "./VideoTaskbar";
import VideoBento from "./VideoBento";

const Videos = () => {
  const [inputValue, setInputValue] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState("realistic");
  const [videos, setVideos] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [videosPerPage] = useState(16);
  const [totalVideos, setTotalVideos] = useState(0);
  const [processingJobId, setProcessingJobId] = useState(null);
  const [selectedModel, setSelectedModel] = useState("Standard");
  const [selectedDuration, setSelectedDuration] = useState(5);
  const [isImageMode, setIsImageMode] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [selectedEffect, setSelectedEffect] = useState(null);
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const intervalRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle navigation state from image page
  useEffect(() => {
    if (location.state?.imageUrl) {
      setImageUrl(location.state.imageUrl);
      setIsImageMode(true);
      // Clear the location state after setting the image
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", checkIfMobile);
    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  const handleCreate = async () => {
    if (!inputValue.trim() && !selectedEffect) {
      alert("Please enter a prompt or select an effect.");
      return;
    }

    if (isImageMode && !uploadedImage && !imageUrl) {
      alert("Please upload an image or provide an image URL.");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('model', selectedModel);
      formData.append('duration', selectedDuration);

      // Handle image upload
      if (imageUrl && !uploadedImage) {
        // If we have a URL but no file, use the URL
        formData.append('image_url', imageUrl);
      } else if (uploadedImage) {
        // If we have a file, send the file
        formData.append('image', uploadedImage);
      }
      
      if (selectedEffect) {
        formData.append('effect', selectedEffect);
      } else {
        formData.append('input', inputValue);
        formData.append('style', selectedStyle);
        if (!isImageMode) {
          formData.append('aspectRatio', aspectRatio);
        }
      }

      const response = await fetchWithTokenRefresh(
        "https://api.vidstudio.ai/trigger-clip",
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (response.ok) {
        // Trigger credit update after successful generation
        triggerCreditUpdate();
        
        setProcessingJobId(data.jobId);
        setVideos(prevVideos => [{
          jobId: data.jobId,
          status: 'Processing',
          startedAt: new Date().toISOString(),
          inputPrompt: inputValue,
          style: selectedStyle,
          model: selectedModel,
          duration: selectedDuration,
          effect: selectedEffect
        }, ...prevVideos]);
        
        if (data.jobId) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          intervalRef.current = setInterval(() => {
            pollVideoStatus(data.jobId);
          }, 10000);
        }
        
        setInputValue("");
        setUploadedImage(null);
        setImageUrl(null);
        setSelectedEffect(null);

        // Clean up any preview URLs
        const previewEl = document.querySelector('.image-preview');
        if (previewEl) {
          URL.revokeObjectURL(previewEl.src);
          previewEl.src = '';
        }
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error("Error creating:", error);
      alert("Failed to create video.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVideos = async (page = 1, limit = 16) => {
    try {
      const response = await fetchWithTokenRefresh(
        `https://api.vidstudio.ai/user/videos?page=${page}&limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setVideos(prevVideos => {
          const videosMap = new Map();
          prevVideos.forEach(video => videosMap.set(video.jobId, video));
          data.videos.forEach(video => videosMap.set(video.jobId, video));
          return Array.from(videosMap.values()).sort((a, b) => 
            new Date(b.startedAt) - new Date(a.startedAt)
          );
        });
        setTotalVideos(data.total);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const pollVideoStatus = async (jobId) => {
    try {
      const response = await fetchWithTokenRefresh(
        `https://api.vidstudio.ai/user/videos?page=1&limit=${videosPerPage}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await response.json();
      
      if (response.ok) {
        const updatedVideo = data.videos.find(video => video.jobId === jobId);
        
        if (updatedVideo) {
          setVideos(prevVideos => {
            const updatedVideos = prevVideos.map(video => 
              video.jobId === jobId ? updatedVideo : video
            );
            return updatedVideos.sort((a, b) => 
              new Date(b.startedAt) - new Date(a.startedAt)
            );
          });
          
          if (updatedVideo.status !== 'Processing') {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            setProcessingJobId(null);
          }
        }
      }
    } catch (error) {
      console.error('Error polling video status:', error);
    }
  };

  useEffect(() => {
    fetchVideos(currentPage, videosPerPage);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentPage, videosPerPage]);

  const handleLoadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <DashHeader />
        <main className="flex-1 overflow-y-auto">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <div className="flex flex-col gap-8">
                <VideoTaskbar
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  selectedStyle={selectedStyle}
                  setSelectedStyle={setSelectedStyle}
                  selectedModel={selectedModel}
                  setSelectedModel={setSelectedModel}
                  selectedDuration={selectedDuration}
                  setSelectedDuration={setSelectedDuration}
                  isImageMode={isImageMode}
                  setIsImageMode={setIsImageMode}
                  uploadedImage={uploadedImage}
                  setUploadedImage={setUploadedImage}
                  imageUrl={imageUrl}
                  setImageUrl={setImageUrl}
                  selectedEffect={selectedEffect}
                  setSelectedEffect={setSelectedEffect}
                  aspectRatio={aspectRatio}
                  setAspectRatio={setAspectRatio}
                  handleCreate={handleCreate}
                  isLoading={isLoading}
                />
                <VideoBento
                  videos={videos}
                  currentPage={currentPage}
                  videosPerPage={videosPerPage}
                  totalVideos={totalVideos}
                  handleLoadMore={handleLoadMore}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Videos; 